import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CoachProfileCell from 'app/components/CustomDataGrid/Cells/CoachProfileCell';
import IdProfileCell from 'app/components/CustomDataGrid/Cells/IdProfileCell';
import TeamMainContactCell from 'app/components/CustomDataGrid/Cells/MainContactCell';
import TeamStatusCell from 'app/components/CustomDataGrid/Cells/StatusCell';
import TeamNameCell from 'app/components/CustomDataGrid/Cells/TeamNameCell';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import React, { useState } from 'react';
import { GeneralLookup, TeamsWrapper } from 'services/openapi';
import styled from 'styled-components';
import { useNavigateToRoute } from 'utils/common';
import TeamContactAddressCell from 'app/components/CustomDataGrid/Cells/ContactAddressCell';
import { useLocation } from 'react-router-dom';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import HorizontalKebabCell from 'app/components/CustomDataGrid/Cells/HorizontalKebabCell';
import { usePermission } from 'hooks/usePermission';
import PaymentStatusCell from 'app/components/CustomDataGrid/Cells/PaymentStatusCell';
import Cookies from 'js-cookie';
import ShowRiderStateCell from 'app/components/CustomDataGrid/Cells/ShowRiderStateCell';
import DynamicTitleCell from 'app/components/CustomDataGrid/Cells/DynamicTitleCell';

type Props = {
  teamProfileData?: TeamsWrapper;
  teamActive?: TeamsWrapper;
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  fetchManageGetTeams: (
    searchInput?: string | undefined,
    status?: string | undefined,
    seasonId?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
    paidFilter?: number,
  ) => void;
  teamActiveOrInactive: (
    id: string,
    activeStatusId?: string | undefined,
    seasonId?: string | undefined,
    searchInput?: string | undefined,
    status?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
    item?: string,
  ) => void;
  debounceTeamSearch: string;
  deleteTeam: (
    id?: string | undefined,
    searchInput?: string | undefined,
    status?: string | undefined,
    seasonId?: string,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  selectedItemId: string | null;
  lookup: GeneralLookup[];
  sortField: string | undefined;
  sortOrder: number | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  paidFilter?: number | null;
  pageSize: number;
  pageSizeSelected: GeneralLookup;
};

const TeamsTable = ({
  teamProfileData,
  setPageSkip,
  pageSkip,
  debounceTeamSearch,
  fetchManageGetTeams,
  teamActiveOrInactive,
  deleteTeam,
  selectedItemId,
  lookup,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading,
  paidFilter,
  pageSize,
  pageSizeSelected
}: Props) => {
  const redirect = useNavigateToRoute();
  const currentLocation = useLocation();
  const { hasViewPermission } = usePermission();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const [teamID, setTeamID] = useState('');
  const activeId = lookup.find(item => item.name === 'Active');
  const inActiveId = lookup.find(item => item.name === 'Inactive');
  const initialSort: Array<SortDescriptor> = [
    { field: 'teamName', dir: 'asc' },
  ];
  const [sort, setSort] = React.useState<Array<SortDescriptor>>(initialSort);
  const userType = Cookies.get('userType') ?? '';
  const handleRowClick = () => { };
  const handleMoreClick = (item: string, e) => {
    const id = e.dataItem.id;

    if (item === 'Make active') {
      if (hasViewPermission('0200101010')) {
        teamActiveOrInactive(
          id,
          activeId?.id,
          e.dataItem.season?.id,
          debounceTeamSearch,
          selectedItemId ?? '',
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
          item,
        );
      }
    } else if (item === 'Make inactive') {
      if (hasViewPermission('0200101010')) {
        teamActiveOrInactive(
          id,
          inActiveId?.id,
          e.dataItem.season?.id,
          debounceTeamSearch,
          selectedItemId ?? '',
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
          item,
        );
      }
    } else if (item === 'Delete team') {
      if (hasViewPermission('0200101011')) {
        setTeamID(id);
        setShowDeletePopup(true);
      }
    } else if (item === 'Edit team info') {
      if (hasViewPermission('0200101009')) {
        redirect(
          userType === 'COACH'
            ? `/coach/teams/${'edit'}/profile-info/${id}`
            : `/admin/teams/${'edit'}/profile-info/${id}`,
        );
      }
    }
  };

  const handleViewClick = id => {
    if (isPastDataFlag) {
      redirect(`/admin/past-data/teams/${`view`}/profile-info/${id}`);
    } else {
      if (hasViewPermission('0200101003')) {
        redirect(
          userType === 'COACH'
            ? `/coach/teams/${`view`}/profile-info/${id}`
            : `/admin/teams/${`view`}/profile-info/${id}`,
        );
      }
    }
  };

  const handleDeleteTeam = () => {
    const currentItemCount = teamProfileData?.teams?.length ?? 0;

    // Calculate next page skip if we're deleting the last item on this page
    let nextPageSkip = pageSkip;
    if (currentItemCount <= 1 && pageSkip && pageSkip > 0) {
      nextPageSkip = pageSkip - pageSize;
      setPageSkip(nextPageSkip);
    }

    deleteTeam(
      teamID,
      debounceTeamSearch,
      selectedItemId ?? '',
      '',
      sortField,
      sortOrder,
      nextPageSkip ?? 0,
      pageSize,
    );
    setShowDeletePopup(false);
  };
  const handleSortChange = e => {
    let sortOrderValue = e.dir === 'asc' ? 0 : 1;
    setSortOrder(sortOrderValue);
    setSortField(e.field);
    fetchManageGetTeams(
      debounceTeamSearch,
      selectedItemId ?? '',
      '',
      e.field,
      sortOrderValue,
      pageSkip,
      pageSize,
      paidFilter as number,
    );
  };

  const TeamsColumn = [
    {
      field: 'action',
      title: 'Action',
      cell: e => (
        <HorizontalKebabCell
          isAssigned={true}
          e={e}
          onMoreClick={handleMoreClick}
          isPastDataFlag={isPastDataFlag}
        />
      ),
      width: isPastDataFlag ? '0' : '150',
      sortable: false,
    },
    {
      field: 'teamId',
      title: 'Team ID',
      cell: e => <IdProfileCell e={e} onClick={handleRowClick} />,
      width: '90',
      sortable: false,
    },
    {
      field: 'teamName',
      title: 'Team Name',
      cell: e => (
        <TeamNameCell
          e={e}
          onClick={handleRowClick}
          onViewClick={event => handleViewClick(event)}
        />
      ),
      width: '220',
      locked: true,
      sortable: true,
      className: 'team-name-style',
      headerClassName: 'team-header-style',
    },

    {
      field: 'coach',
      title: 'Coach (s)',
      cell: e => <CoachProfileCell e={e} onClick={handleRowClick} />,
      width: '250',
      sortable: true,
      headerClassName: 'coach-header-style',
    },
    {
      field: '',
      title: 'Main Contact',
      cell: e => <TeamMainContactCell e={e} onClick={handleRowClick} />,
      width: '200',
      sortable: false,
    },
    {
      field: 'contactAddress',
      title: 'Contact Address',
      cell: e => <TeamContactAddressCell e={e} onClick={handleRowClick} />,
      width: '180',
      sortable: false,
    },
    {
      field: 'city',
      title: 'City',
      cell: e => (
        <DynamicTitleCell
          e={e}
          onClick={handleRowClick}
          title={e.dataItem.city}
        />
      ),
      width: '150',
      sortable: false,
    },
    {
      field: 'state',
      title: 'State',
      cell: e => (
        <DynamicTitleCell
          e={e}
          onClick={handleRowClick}
          title={e.dataItem.state}
        />
      ),
      width: '150',
      sortable: false,
    },
    // {
    //   field: 'isPaymentComplete',
    //   title: 'Payment Status',
    //   cell: e => <PaymentStatusCell e={e} onClick={handleRowClick} />,
    //   width: '180',
    //   sortable: false,
    // },
    // {
    //   field: 'financialRespSignature',
    //   title: 'Financial Resp Signature',
    //   width: '200',
    //   sortable: false,
    // },
    {
      field: 'teamStatus',
      title: 'Status',
      cell: e => <TeamStatusCell e={e} onClick={handleRowClick} />,
      width: isPastDataFlag ? '100' : '150',
      sortable: false,
    },
  ];
  const TeamsPastDataColumn = TeamsColumn.slice(0, -1);
  const handlePageChange = event => {
    setPageSkip(event.page.skip);
    fetchManageGetTeams(
      debounceTeamSearch,
      selectedItemId ?? '',
      '',
      sortField,
      sortOrder,
      event.page.skip,
      pageSize,
      paidFilter as number,
    );
  };

  // Reset pagination when pageSize changes
  React.useEffect(() => {
    setSort([{ field: 'teamName', dir: 'asc' }]);
  }, [pageSize]);

  return (
    <>
      {loading ? (
        <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
      ) : (
        <>
          <CardWrapper style={{ paddingTop: '15px' }}>
            <CardSubWrapper className="d-flex"></CardSubWrapper>
            <GridWrapper>
              <CustomDataGrid
                showPagination={(teamProfileData?.pageDetails?.count ?? 0) > Number(pageSizeSelected.id)}
                data={teamProfileData?.teams ?? []}
                columns={isPastDataFlag ? TeamsPastDataColumn : TeamsColumn}
                pageSize={pageSize}
                totalCount={teamProfileData?.pageDetails?.count}
                onPageChange={event => {
                  handlePageChange(event);
                }}
                skip={pageSkip}
                sortable={{
                  allowUnsort: false,
                  mode: 'single',
                }}
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => {
                  let sortvalue = e.sort[0];
                  setSort(e.sort);
                  handleSortChange(sortvalue);
                }}
              />
            </GridWrapper>
          </CardWrapper>
          <CustomDeletePopup
            deleteIcon={true}
            headerUnderline={false}
            popupSubLabel="Are you sure you want to delete this team?"
            className="body-scroll-lock height-full delete-update"
            popupLabel="Delete team"
            setVisible={setShowDeletePopup}
            visible={showDeletePopup}
            align="centre"
            btnLabel1="Cancel"
            btnLabel2="Delete"
            handlebtn2Click={handleDeleteTeam}
            handlebtn1Click={() => {
              setShowDeletePopup(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default TeamsTable;

const GridWrapper = styled.div`
  .k-grid-container {
    height: calc(100vh - 255px);
  }
  .k-table-thead {
    .k-link {
      cursor: default;
    }
    .coach-header-style,
    .team-header-style {
      cursor: pointer !important;
      .k-link {
        cursor: pointer !important;
      }
    }
  }
`;
